import { useSelector, useDispatch } from "react-redux";
import { IStore } from "@/redux/defaultStore";
import useGetGuildOwnerships from "@/hooks/useGetGuildOwnerships";
import { useSearchParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useGetCompetitionInfo } from "@/hooks/useGetCompetitionInfo";
import { InviteModal, InviteModalType } from "@/components/InviteModal";
import GuildInfoDrawer from "./guilds/GuildInfoDrawer";
import { useGetGuild } from "@/hooks/useGetGuild";
import { toggleLoginOpen } from "@/redux/meta/metaActions";
import { useEffect, useState } from "react";
import UnifiedLeaderboardV2 from "./guilds/UnifiedLeaderboardV2";
import GuildHeaderCockpit from "./guilds/GuildHeaderCockpit";
import GuildSponsorshipBanner from "./guilds/GuildSponsorshipBanner";
import MyPOPBadgesCockpit from "./guilds/MyPOPBadgesCockpit";
import GuildSearchCockpit from "./guilds/GuildSearchCockpit";
import { ViewMode } from "@/components/goFrens/GoFrensTopLeaderboard";
import LeaveGuildModal from "@/components/modals/guilds/LeaveGuildModal";

export default function GuildsCockpitPage() {
    const [searchParams] = useSearchParams();
    const dispatch = useDispatch();
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const {data: guildOwnerships, refetch: refetchGuildOwnerships, isLoading: isGuildOwnershipLoading} = useGetGuildOwnerships(fullToken, true);
    const queryClient = useQueryClient();
    const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
    const [isLeaveGuildModalOpen, setIsLeaveGuildModalOpen] = useState(false);
    const [viewMode, setViewMode] = useState<ViewMode>(ViewMode.MONTHLY);

    const guildInviteId = searchParams.get("guildId");
    const hasInvite = searchParams.has("ref");
    const {data: refGuildInfo} = useGetGuild(guildInviteId);
    const [isRefGuildModalOpen, setIsRefGuildModalOpen] = useState(!!guildInviteId);

    const showCompetitions = searchParams.get("show-competitions");
    const {data: competitionInfo} = useGetCompetitionInfo(showCompetitions === "false");

    const currentGuild = guildOwnerships?.qualifiedGuilds.find(guild => guild.id === guildOwnerships.membership?.guild);

    useEffect(() => {
        void refetchGuildOwnerships();
        queryClient.invalidateQueries({ queryKey: ["top-guilds-leaderboard"]});
        queryClient.invalidateQueries({ queryKey: ["top-guild-leaders-leaderboard"]});
        queryClient.invalidateQueries({ queryKey: ["current-guild-top-members"]});
        if (hasInvite && guildInviteId && !fullToken) {
            dispatch(toggleLoginOpen(true));
        }
    }, []);

    const handleOpenInviteModal = () => {
        if (!fullToken) {
            dispatch(toggleLoginOpen(true));
        } else {
            setIsInviteModalOpen(true);
        }
    };

    const [isGuildInfoDrawerOpen, setIsGuildInfoDrawerOpen] = useState(false);

    const handleOpenGuildInfo = () => {
        setIsGuildInfoDrawerOpen(true);
    };

    const handleLeaveGuild = () => {
        setIsLeaveGuildModalOpen(true);
    };

    const handleBrowseGuilds = () => {
    };

    return (
        <div className="guilds-cockpit-page">
            <InviteModal
                isVisible={isInviteModalOpen}
                type={InviteModalType.GUILD}
                onClose={() => setIsInviteModalOpen(false)}
                guildId={currentGuild?.id}
            />

            {currentGuild &&
                <LeaveGuildModal
                    guild={currentGuild}
                    isOpen={isLeaveGuildModalOpen}
                    toggle={() => setIsLeaveGuildModalOpen(!isLeaveGuildModalOpen)}
                />
            }

            {refGuildInfo &&
                <GuildInfoDrawer
                    isOpen={isRefGuildModalOpen}
                    toggle={() => setIsRefGuildModalOpen(false)}
                    guild={refGuildInfo}
                />
            }

            {currentGuild &&
                <GuildInfoDrawer
                    isOpen={isGuildInfoDrawerOpen}
                    toggle={() => setIsGuildInfoDrawerOpen(false)}
                    guild={currentGuild}
                />
            }

            {/* Our custom guild header */}
            <GuildHeaderCockpit
                guild={currentGuild}
                topGuilds={null}
                isLoading={isGuildOwnershipLoading}
                onOpenInvite={handleOpenInviteModal}
                onOpenAbout={handleOpenGuildInfo}
                onLeaveGuild={handleLeaveGuild}
                onBrowseGuilds={handleBrowseGuilds}
                viewMode={viewMode}
                onViewModeChange={setViewMode}
            />

            {/* Sponsorship & Monthly Prizes Banner */}
            <GuildSponsorshipBanner
                competition={competitionInfo?.currentCompetition}
                onViewRules={() => {
                }}
                onViewPrizes={() => {
                }}
            />

            {/* Unified Leaderboard with all competitive views */}
            <UnifiedLeaderboardV2 currentGuildId={guildOwnerships?.membership?.guild} />

            {/* My POP Badges Section */}
            <MyPOPBadgesCockpit
                badges={guildOwnerships?.badges || []}
                onViewAll={() => {
                }}
            />

            {/* Guild Search Section */}
            <GuildSearchCockpit
                guilds={[
                    ...guildOwnerships?.qualifiedGuilds || [],
                    ...guildOwnerships?.otherGuilds || [],
                ]}
                badges={guildOwnerships?.badges || []}
            />
        </div>
    );
}